/**
 * Google Analytics 4 for the public pages only.
 *
 * The measurement script is loaded only for a signed-out visitor on the
 * landing page, the beta application or the picture manual, and never for a
 * browser that asks not to be tracked. Classroom pages (and the student
 * handout at /manual/student) never load it; when a page that loaded it signs
 * in, the shell reloads the page so the script does not stay in memory.
 *
 * Nothing here may throw: a failed measurement must never block an
 * application.
 */
import type { AuthState } from "../auth/AuthProvider";
import type { Route } from "../router";

type RouteName = Route["name"];

/** The only routes measured, and only while signed out. */
export const PUBLIC_ANALYTICS_ROUTES: ReadonlySet<RouteName> = new Set([
  "home",
  "beta",
  "manual",
]);

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

export type AnalyticsConditions = {
  measurementId: string | null | undefined;
  /** null when the route shows something other than its public page. */
  routeName: RouteName | null;
  authStatus: AuthState["status"];
  /** The browser sends Do Not Track or Global Privacy Control. */
  privacySignal: boolean;
};

/** Whether this page may load and send to Google Analytics. */
export const shouldMeasure = ({
  measurementId,
  routeName,
  authStatus,
  privacySignal,
}: AnalyticsConditions): boolean =>
  typeof measurementId === "string" &&
  MEASUREMENT_ID_PATTERN.test(measurementId) &&
  routeName !== null &&
  PUBLIC_ANALYTICS_ROUTES.has(routeName) &&
  authStatus === "anonymous" &&
  !privacySignal;

type PrivacyNavigator = {
  doNotTrack?: string | null;
  msDoNotTrack?: string | null;
  globalPrivacyControl?: boolean;
};

/** Do Not Track ("1") or Global Privacy Control, wherever the browser puts it. */
export const hasPrivacySignal = (
  nav: PrivacyNavigator | undefined = typeof navigator === "undefined"
    ? undefined
    : (navigator as PrivacyNavigator),
  win: { doNotTrack?: string | null } | undefined = typeof window ===
  "undefined"
    ? undefined
    : (window as { doNotTrack?: string | null }),
): boolean =>
  nav?.doNotTrack === "1" ||
  nav?.msDoNotTrack === "1" ||
  nav?.globalPrivacyControl === true ||
  win?.doNotTrack === "1";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = typeof UTM_KEYS[number];
export type Utm = Partial<Record<UtmKey, string>>;

const UTM_VALUE = /^[A-Za-z0-9_\-.]{1,100}$/;

/** The page address sent to Google: origin and path plus utm_ parameters. */
export const publicPageLocation = (
  origin: string,
  pathname: string,
  search: string,
): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith("utm_")) {
      kept.append(key, value);
    }
  });
  const query = kept.toString();
  return `${origin}${pathname}${query ? `?${query}` : ""}`;
};

/** The five campaign tags, keeping only short values of safe characters. */
export const sanitizeUtm = (search: string): Utm | null => {
  const params = new URLSearchParams(search);
  const utm: Utm = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value !== null && UTM_VALUE.test(value)) {
      utm[key] = value;
    }
  }
  return Object.keys(utm).length > 0 ? utm : null;
};

export const UTM_STORAGE_KEY = "cv_utm";

/** Keeps the first campaign tags of this tab's visit (first touch wins). */
export const rememberUtm = (
  search: string,
  storage: Pick<Storage, "getItem" | "setItem"> | undefined = typeof window ===
  "undefined"
    ? undefined
    : window.sessionStorage,
): void => {
  try {
    if (!storage || storage.getItem(UTM_STORAGE_KEY) !== null) {
      return;
    }
    const utm = sanitizeUtm(search);
    if (utm) {
      storage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
  } catch {
    // Storage can be blocked; campaign tags are optional.
  }
};

type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: Gtag;
} & Record<string, unknown>;

const GA_SCRIPT_ORIGIN = "https://www.googletagmanager.com";

const configuredMeasurementId = (): string | undefined => {
  const value = import.meta.env.VITE_APP_GA4_MEASUREMENT_ID;
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

/** The id whose script this page has loaded, if any. */
let loadedId: string | null = null;
/** Whether the current page passed the conditions at its last check. */
let active = false;

export const isPublicAnalyticsLoaded = (): boolean => loadedId !== null;

const disableKey = (id: string) => `ga-disable-${id}`;

const loadOnce = (win: AnalyticsWindow, id: string): Gtag => {
  if (loadedId === id && typeof win.gtag === "function") {
    return win.gtag;
  }
  win.dataLayer = win.dataLayer || [];
  const gtag: Gtag = function () {
    // gtag.js reads the arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer!.push(arguments);
  };
  win.gtag = gtag;
  gtag("js", new Date());
  gtag("config", id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `${GA_SCRIPT_ORIGIN}/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  loadedId = id;
  return gtag;
};

/**
 * Re-checks the conditions after a route or sign-in change: loads the script
 * the first time they hold and sends a page view, or switches sending off.
 */
export const syncPublicAnalytics = (
  {
    routeName,
    authStatus,
  }: Pick<AnalyticsConditions, "routeName" | "authStatus">,
  measurementId: string | undefined = configuredMeasurementId(),
): void => {
  try {
    const win = window as unknown as AnalyticsWindow;
    const measure = shouldMeasure({
      measurementId,
      routeName,
      authStatus,
      privacySignal: hasPrivacySignal(),
    });
    if (!measure || !measurementId) {
      active = false;
      if (measurementId) {
        win[disableKey(measurementId)] = true;
      }
      return;
    }
    const { origin, pathname, search } = window.location;
    rememberUtm(search);
    const gtag = loadOnce(win, measurementId);
    win[disableKey(measurementId)] = false;
    active = true;
    gtag("event", "page_view", {
      page_location: publicPageLocation(origin, pathname, search),
      page_path: pathname,
      page_title: document.title,
    });
    if (routeName === "manual") {
      gtag("event", "manual_view");
    }
  } catch {
    active = false;
  }
};

export type AnalyticsEvent = "beta_cta_click" | "beta_submit";

/** Sends an event on a measured page; silently does nothing otherwise. */
export const track = (
  name: AnalyticsEvent,
  params: Record<string, string> = {},
): void => {
  try {
    const gtag = (window as unknown as AnalyticsWindow).gtag;
    if (!active || loadedId === null || typeof gtag !== "function") {
      return;
    }
    gtag("event", name, params);
  } catch {
    // Measurement never interrupts the visitor.
  }
};

/** Test helper: forgets the loaded script and the last check. */
export const resetPublicAnalyticsForTests = (): void => {
  loadedId = null;
  active = false;
};
